import React from "react";
import { useNavigate } from "react-router-dom";
import "../CSS/Pricing.css";
import {
  FiCheck,
  FiArrowRight,
  FiZap,
  FiTrendingUp,
  FiLayers,
  FiPackage,
  FiShoppingCart,
  FiCode,
  FiSmartphone,
  FiStar,
  FiTarget,
} from "react-icons/fi";

const Pricing = () => {
  const navigate = useNavigate();

  const automationPlans = [
    {
      id: 1,
      name: "Essential",
      description: "Perfect for small teams getting started",
      price: "₹1,600",
      period: "per month",
      icon: <FiZap />,
      popular: false,
      features: [
        "Basic Automation",
        "500 Tasks/Month",
        "Email Support",
        "2 Workflows",
      ],
    },
    {
      id: 2,
      name: "Professional",
      description: "For growing businesses",
      price: "₹3,000",
      period: "per month",
      icon: <FiTrendingUp />,
      popular: true,
      features: [
        "Advanced Automation",
        "2,000 Tasks/Month",
        "Priority Support",
        "10 Workflows",
        "Analytics & Reports",
      ],
    },
    {
      id: 3,
      name: "Business",
      description: "For established businesses",
      price: "₹15,000",
      period: "per month",
      icon: <FiLayers />,
      popular: false,
      features: [
        "Full Automation Suite",
        "10,000 Tasks/Month",
        "24/7 Support",
        "Unlimited Workflows",
        "Advanced Analytics",
        "Dedicated Manager",
      ],
    },
    {
      id: 4,
      name: "Enterprise",
      description: "For large organizations",
      price: "₹30,000",
      period: "per month",
      icon: <FiTarget />,
      popular: false,
      features: [
        "Complete Automation",
        "Unlimited Tasks",
        "Dedicated Support Team",
        "Unlimited Workflows",
        "Custom Integrations",
        "Advanced Security",
      ],
    },
  ];

  const websitePricing = {
    icon: <FiCode />,
    title: "Website Development",
    subtitle: "Professional websites tailored to your business",
    price: "Starting at ₹20,000",
    description: "One-time cost",
    note: "Domain, hosting, and external service charges are exclusive",
    features: [
      "Custom Design & Development",
      "Responsive & Mobile-Friendly",
      "SEO-Optimized Structure",
      "Content Management System",
      "Contact Forms & Integrations",
      "Performance Optimization",
      "Basic Analytics Setup",
      "Post-Launch Support",
    ],
  };

  const appPricing = {
    icon: <FiSmartphone />,
    title: "Mobile App Development",
    subtitle: "Native iOS and Android applications",
    price: "Starting at ₹40,000",
    description: "One-time cost for both platforms",
    note: "App Store fees ($99/year), Play Store fees ($25 one-time), domain, and external service charges are exclusive",
    features: [
      "iOS & Android Native Apps",
      "Custom UI/UX Design",
      "Cross-Platform Compatibility",
      "Push Notifications",
      "API Integration",
      "App Store Submission Assistance",
      "Performance Testing",
      "Post-Launch Support",
    ],
  };

  const ecommercePackages = [
    {
      id: 1,
      name: "Starter Package",
      icon: <FiShoppingCart />,
      subtitle: "Shopify Store Setup & Branding",
      popular: false,
      features: [
        "Shopify store setup & theme customization",
        "Domain & branded email configuration",
        "Brand identity setup (colors, fonts, banners)",
        "Initial product catalog upload",
        "Basic discount strategy for first-time buyers",
        "Payment gateway & shipping integration",
      ],
    },
    {
      id: 2,
      name: "Growth Package",
      icon: <FiPackage />,
      subtitle: "Automation & Retention",
      popular: true,
      features: [
        "WhatsApp, Email & SMS automation flows",
        "Customer loyalty & retention discount strategy",
        "Shipment solution integration (Shiprocket, NimbusPost)",
        "Custom Shopify reports & dashboards",
        "Google Analytics 4 & Pixel integration",
        "SEO basics & product optimization",
      ],
    },
    {
      id: 3,
      name: "Premium Package",
      icon: <FiStar />,
      subtitle: "Full-Funnel Growth",
      popular: false,
      features: [
        "Advanced Shopify theme customization",
        "Product picture graphic generation & visuals",
        "AI-driven automation flows (browse/cart abandonment, loyalty)",
        "Social media integration (Instagram Shop, FB Shop, Pinterest)",
        "Advanced analytics: LTV, cohorts, retention",
        "Festival & sales campaign strategies",
      ],
    },
  ];

  const ecommerceAddons = [
    "Custom landing pages for campaigns",
    "Advanced SEO & blog setup",
    "Influencer/review automation",
    "Multi-channel selling (Amazon, Flipkart)",
    "WhatsApp chatbot for 24/7 support",
  ];

  const faqData = [
    {
      id: 1,
      question: "Can I upgrade my automation plan later?",
      answer:
        "Absolutely! You can upgrade your automation plan at any time. The price difference will be prorated, and you'll get immediate access to the upgraded features.",
    },
    {
      id: 2,
      question: "What's included in website and app pricing?",
      answer:
        "Our website pricing starts at ₹20,000 and includes custom design, development, and basic features. Mobile apps are ₹40,000 for both iOS and Android. External costs like domain, hosting, and store fees are additional.",
    },
    {
      id: 3,
      question: "How do the e-commerce packages work?",
      answer:
        "Our e-commerce packages are designed for Shopify stores. Choose a package based on your growth stage - Starter for setup, Growth for automation, or Premium for advanced marketing. Add-on services are available separately.",
    },
    {
      id: 4,
      question: "Do you offer custom solutions?",
      answer:
        "Yes! If our standard packages don't fit your needs, we offer fully customized solutions. Contact us to discuss your specific requirements and get a tailored quote.",
    },
    {
      id: 5,
      question: "What payment terms do you offer?",
      answer:
        "For monthly automation plans, payment is billed monthly. For one-time projects (websites, apps, e-commerce), we typically work with milestone-based payments or can customize terms based on your needs.",
    },
  ];

  const handleGetStarted = () => {
    navigate("/contact");
  };

  return (
    <>
      <title>Pricing | Web, Android & Automation Services - Syncnode</title>
      <meta name="description" content="Transparent pricing for business automation starting at ₹1,600/month, website development from ₹20,000, Android apps from ₹40,000, and e-commerce solutions. No hidden fees." />
      <meta name="keywords" content="IT services pricing, web development cost, android app pricing, automation pricing, e-commerce packages, Shopify setup, custom software pricing, affordable web development" />
      <link rel="canonical" href="https://www.syncnode.in/pricing" />
      <meta property="og:title" content="Pricing | Web, Android & Automation Services - Syncnode" />
      <meta property="og:description" content="Simple, transparent pricing. Automation plans from ₹1,600/month, websites from ₹20,000, Android apps from ₹40,000. Engineering Digital Excellence." />
      <meta property="og:url" content="https://www.syncnode.in/pricing" />
      <meta property="og:type" content="website" />
      <div className="pricing_wrapper">
        <div className="container">
          <div className="pricing_banner">
            <h1>
              Simple, <span className="decorated_text">Transparent</span>{" "}
              Pricing
            </h1>
            <p className="intro_text">
              Choose the perfect plan for your project. No hidden fees, no
              surprises. Just quality development and exceptional support.
            </p>
          </div>

          <div className="automation_section">
            <p className="sub_title">Automation</p>
            <h1 className="title">Workflow Automation Plans</h1>
            <p className="section_intro">
              Streamline your business operations with intelligent automation
              solutions.
            </p>

            <div className="plans_row">
              {automationPlans.map((plan) => (
                <div
                  className={`plan_item ${plan.popular ? "popular" : ""}`}
                  key={plan.id}
                >
                  {plan.popular && (
                    <div className="popular_badge">Most Popular</div>
                  )}
                  <div className="plan_icon">{plan.icon}</div>
                  <h3>{plan.name}</h3>
                  <p className="plan_description">{plan.description}</p>
                  <div className="price_section">
                    <h2 className="price">{plan.price}</h2>
                    <p className="period">{plan.period}</p>
                  </div>
                  <button
                    className="btn primary_button full_width"
                    onClick={handleGetStarted}
                  >
                    Get Started <FiArrowRight />
                  </button>
                  <div className="features_list">
                    <p className="features_title">What's included:</p>
                    <ul>
                      {plan.features.map((feature, index) => (
                        <li key={index}>
                          <FiCheck className="check_icon" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="onetime_pricing_section">
            <p className="sub_title">One-Time Projects</p>
            <h1 className="title">Web & Mobile Development</h1>
            <p className="section_intro">
              Professional development services with transparent one-time
              pricing.
            </p>

            <div className="onetime_row">
              <div className="onetime_card">
                <div className="onetime_icon">{websitePricing.icon}</div>
                <h3>{websitePricing.title}</h3>
                <p className="onetime_subtitle">{websitePricing.subtitle}</p>
                <div className="onetime_price_section">
                  <h2 className="price">
                    Starting at
                    <span>₹20,000</span>
                  </h2>
                  <p className="description">{websitePricing.description}</p>
                  <p className="note">{websitePricing.note}</p>
                </div>
                <button
                  className="btn primary_button full_width"
                  onClick={handleGetStarted}
                >
                  Get Quote <FiArrowRight />
                </button>
                <div className="features_list">
                  <ul>
                    {websitePricing.features.map((feature, index) => (
                      <li key={index}>
                        <FiCheck className="check_icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="onetime_card">
                <div className="onetime_icon">{appPricing.icon}</div>
                <h3>{appPricing.title}</h3>
                <p className="onetime_subtitle">{appPricing.subtitle}</p>
                <div className="onetime_price_section">
                  <h2 className="price">
                    Starting at
                    <span>₹40,000</span>
                  </h2>
                  <p className="description">{appPricing.description}</p>
                  <p className="note">{appPricing.note}</p>
                </div>
                <button
                  className="btn primary_button full_width"
                  onClick={handleGetStarted}
                >
                  Get Quote <FiArrowRight />
                </button>
                <div className="features_list">
                  <ul>
                    {appPricing.features.map((feature, index) => (
                      <li key={index}>
                        <FiCheck className="check_icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="ecommerce_section">
            <p className="sub_title">E-Commerce</p>
            <h1 className="title">E-Commerce Solutions</h1>
            <p className="section_intro">
              Your Shopify Growth Partner - Complete solutions to launch, grow,
              and scale your online store.
            </p>

            <div className="ecommerce_packages_row">
              {ecommercePackages.map((pkg) => (
                <div
                  className={`ecommerce_package ${
                    pkg.popular ? "popular" : ""
                  }`}
                  key={pkg.id}
                >
                  {pkg.popular && (
                    <div className="popular_badge">Most Popular</div>
                  )}
                  <div className="package_icon">{pkg.icon}</div>
                  <h3>{pkg.name}</h3>
                  <p className="package_subtitle">{pkg.subtitle}</p>
                  <button
                    className="btn primary_button full_width"
                    onClick={handleGetStarted}
                  >
                    Get Started <FiArrowRight />
                  </button>
                  <div className="features_list">
                    <ul>
                      {pkg.features.map((feature, index) => (
                        <li key={index}>
                          <FiCheck className="check_icon" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <div className="addons_section">
              <h3>🎯 Add-On Services</h3>
              <p>Enhance your e-commerce store with these additional services:</p>
              <div className="addons_list">
                {ecommerceAddons.map((addon, index) => (
                  <div className="addon_item" key={index}>
                    <FiCheck className="addon_icon" />
                    <span>{addon}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="custom_solution_section">
            <div className="custom_solution_content">
              <h2>Need a Custom Solution?</h2>
              <p>
                Every business is unique. If our standard packages don't quite
                fit your needs, we're here to create a tailored solution just
                for you. Get in touch with our team to discuss your specific
                requirements.
              </p>
              <button
                className="btn complementary_button"
                onClick={handleGetStarted}
              >
                Discuss Your Requirements <FiArrowRight />
              </button>
            </div>
          </div>

          <div className="pricing_faq">
            <p className="sub_title">FAQ</p>
            <h1 className="title">Frequently Asked Questions</h1>
            <p className="section_intro">
              Everything you need to know about our pricing and services.
            </p>

            <div className="faq_row">
              {faqData.map((faq) => (
                <div className="faq_item" key={faq.id}>
                  <h4>{faq.question}</h4>
                  <p>{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Pricing;
